/** @format */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Modal,
	Backdrop,
	Fade,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormHelperText,
	Alert,
	alpha,
	Link,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { colors } from "../../utility/types/constant";
import {
	IUserPayload,
	EmptyUserPayload,
	EmptyPhoneContact,
} from "../../utility/types/userType";
import { Formik, Form } from "formik";
import signUpSchema from "../../utility/validation/signUpSchemaValidation";
import { signUp } from "../../redux/actions/userActions";
import { AppDispatch } from "../../redux/store/store";
import { unwrapResult } from "@reduxjs/toolkit";
import { UserTermsModal } from "../allComponents/AllComponents";
import { signUpLoad } from "../../redux/selectors/userSelector";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./SignUp.css";
import { SUPPORTED_COUNTRIES } from "../../utility/stripeConnectCountries";
import { trackSignUp } from "../../utility/redditPixel";

const BENEFITS = [
	"Create or join savings circles with people you trust",
	"Automated contributions and scheduled payouts",
	"Bank-level security powered by Stripe",
	"Available in 140+ countries worldwide",
];

const SignUp = () => {
	const navigate = useNavigate();
	const { t } = useTranslation("auth");

	const dispatch = useDispatch<AppDispatch>();

	const [initialValues, setInitialValues] =
		useState<IUserPayload>(EmptyUserPayload);

	const [signUpSuccess, setSignUpSuccess] = useState(false);

	const [errorMessageAlert, setErrorMessageAlert] = useState("");

	const [showTerms, setShowTerms] = useState(false);

	const [hasSmsConsent, setHasSmsConsent] = useState(false);

	const isLoading = useSelector(signUpLoad);

	const handleShowTerms = () => {
		setShowTerms(true);
	};

	const handleAgreeToTerms = () => {
		setInitialValues({ ...initialValues, hasAgreedToTerms: true });
		setShowTerms(false);
	};

	const handleDisAgreeToTerms = () => {
		setInitialValues({ ...initialValues, hasAgreedToTerms: false });
		setShowTerms(false);
		setErrorMessageAlert(
			t("signUp.errors.termsRequired"),
		);
		setTimeout(() => {
			setErrorMessageAlert("");
		}, 5000);
	};

	const handleCloseShowTerms = () => {
		setShowTerms(false);
	};

	const handleCloseSuccessAlert = () => {
		setSignUpSuccess(false);
	};

	const handleCloseErrorAlert = () => {
		setErrorMessageAlert(""); // Set the error message to an empty string to hide the alert
	};

	const handleSubmit = async (values: IUserPayload) => {
		if (!initialValues.hasAgreedToTerms) {
			return setErrorMessageAlert(
				t("signUp.errors.termsRequired"),
			);
		}

		try {
			const { _id, stripeCustomerId, ...rest } = values;

			// Add SMS consent to phone object if phone number is provided
			const phoneWithConsent =
				rest.phone?.e164 ?
					{
						...rest.phone,
						consent: {
							status:
								hasSmsConsent ? ("opted_in" as const) : ("opted_out" as const),
							source: "signup_form" as const,
							timeStamp: new Date().toISOString(),
						},
					}
					: rest.phone;

			const payload = {
				...rest,
				phone: phoneWithConsent,
				hasAgreedToTerms: initialValues.hasAgreedToTerms,
			};

			const resultAction = await dispatch(signUp(payload));

			if (resultAction) {
				unwrapResult(resultAction);
				setInitialValues({
					...values,
					phone: values.phone || EmptyPhoneContact,
				});
				setSignUpSuccess(true);
				trackSignUp(values.email);

				// redirect user after 2 seconds if they are successfully created and authenticated
				setTimeout(() => {
					navigate("/verify");
				}, 5000);
			} else {
				setErrorMessageAlert("ERROR. User signUp Failed");
				setSignUpSuccess(false);
				setTimeout(() => {
					setErrorMessageAlert("");
				}, 5000);
			}
		} catch (err: any) {
			setErrorMessageAlert(err);
			setSignUpSuccess(false);
			setTimeout(() => {
				setErrorMessageAlert("");
			}, 5000);
		}
	};

	const PhoneTextField = React.forwardRef<HTMLInputElement, any>(
		function PhoneTextField(props, ref) {
			const { value, onChange, ...rest } = props;

			return (
				<TextField
					{...rest}
					inputRef={ref}
					value={value ?? ""}
					onChange={(e) => onChange(e.target.value)}
				/>
			);
		},
	);

	const handleRedirectToSignIn = () => {
		navigate("/circle/sign-in");
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: colors.primary,
				display: "flex",
				position: "relative",
				overflow: "hidden",
			}}
		>
			<CssBaseline />
			{/* Background decorative blobs */}
			<Box
				sx={{
					position: "absolute",
					top: -200,
					right: -200,
					width: 600,
					height: 600,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.07)} 0%, transparent 70%)`,
					pointerEvents: "none",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					bottom: -150,
					left: -150,
					width: 500,
					height: 500,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.05)} 0%, transparent 70%)`,
					pointerEvents: "none",
				}}
			/>

			{/* Success Modal */}
			<Modal
				open={signUpSuccess}
				onClose={handleCloseSuccessAlert}
				closeAfterTransition
				slots={{ backdrop: Backdrop }}
				slotProps={{
					backdrop: {
						timeout: 500,
						sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
					},
				}}>
				<Fade in={signUpSuccess}>
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
							borderRadius: 4,
							boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
							p: 5,
							textAlign: "center",
							minWidth: 320,
							maxWidth: 420,
							outline: "none",
						}}>
						<Box
							sx={{
								width: 80,
								height: 80,
								borderRadius: "50%",
								background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								mx: "auto",
								mb: 3,
								boxShadow: "0 8px 24px rgba(76, 175, 80, 0.4)",
							}}>
							<CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#fff" }} />
						</Box>
						<Typography
							variant="h5"
							component="h2"
							sx={{ fontWeight: 700, color: "#2e7d32", mb: 1 }}>
							{t("signUp.success.message")}
						</Typography>
						<Typography variant="body1" sx={{ color: "#555", mb: 3, lineHeight: 1.6 }}>
							{t("signUp.success.verifyEmail")}
						</Typography>
						<Typography variant="body2" sx={{ color: "#888", fontStyle: "italic" }}>
							Redirecting to verification page...
						</Typography>
						<CircularProgress size={24} sx={{ color: "#4CAF50", mt: 2 }} />
					</Box>
				</Fade>
			</Modal>

			{/* ── Main layout ── */}
			<Box
				sx={{
					display: "flex",
					width: "100%",
					maxWidth: 1100,
					mx: "auto",
					alignItems: "center",
					px: { xs: 2, sm: 3, md: 5 },
					py: { xs: 3, md: 0 },
					minHeight: "100vh",
				}}>

				{/* ── Left branding panel (desktop only) ── */}
				<Box
					sx={{
						display: { xs: "none", lg: "flex" },
						flexDirection: "column",
						justifyContent: "center",
						flex: "0 0 46%",
						pr: 8,
						py: 6,
					}}>
					{/* Logo */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 5 }}>
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: 2,
								overflow: "hidden",
								boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
							}}>
							<Box
								component="img"
								src="/images/how-it-works-images/primary-logo-1-1.png"
								alt="FundsyFi"
								sx={{ width: 42, height: 42, objectFit: "contain", display: "block" }}
							/>
						</Box>
						<Typography
							sx={{
								color: "#fff",
								fontFamily: "'Montserrat', sans-serif",
								fontWeight: 800,
								fontSize: "1.5rem",
								letterSpacing: 0.3,
							}}>
							FundsyFi
						</Typography>
					</Box>
					<Typography
						sx={{
							color: "#fff",
							fontFamily: "'Montserrat', sans-serif",
							fontWeight: 800,
							fontSize: { lg: "2.2rem", xl: "2.6rem" },
							lineHeight: 1.2,
							mb: 2,
						}}>
						Save smarter,{" "}
						<Box component="span" sx={{ color: colors.mainSecondary }}>
							together.
						</Box>
					</Typography>
					<Typography
						sx={{
							color: alpha("#fff", 0.55),
							mb: 4,
							lineHeight: 1.7,
							fontSize: "0.95rem",
							maxWidth: 380,
						}}>
						Join thousands of people building financial futures through trusted savings circles.
					</Typography>
					{BENEFITS.map((benefit, i) => (
						<Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
							<CheckCircleIcon
								sx={{ color: colors.mainSecondary, fontSize: "1.1rem", mt: 0.3, flexShrink: 0 }}
							/>
							<Typography sx={{ color: alpha("#fff", 0.75), fontSize: "0.88rem", lineHeight: 1.55 }}>
								{benefit}
							</Typography>
						</Box>
					))}
				</Box>

				{/* ── Right form card ── */}
				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						py: { xs: 0, lg: 4 },
					}}>
					<Box
						sx={{
							width: "100%",
							maxWidth: 460,
							bgcolor: "#fff",
							borderRadius: { xs: 3, sm: 4 },
							p: { xs: 3, sm: 4 },
							boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
							position: "relative",
							zIndex: 1,
						}}>
						{/* Mobile logo */}
						<Box sx={{ display: { xs: "flex", lg: "none" }, alignItems: "center", gap: 1, mb: 2.5 }}>
							<Box
								sx={{
									width: 32,
									height: 32,
									borderRadius: 1.5,
									overflow: "hidden",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}>
								<Box
									component="img"
									src="/images/how-it-works-images/primary-logo-1-1.png"
									alt="FundsyFi"
									sx={{ width: 32, height: 32, objectFit: "contain", display: "block" }}
								/>
							</Box>
							<Typography
								sx={{
									color: colors.primary,
									fontFamily: "'Montserrat', sans-serif",
									fontWeight: 800,
									fontSize: "1.1rem",
								}}>
								FundsyFi
							</Typography>
						</Box>

						<Typography
							sx={{
								fontFamily: "'Montserrat', sans-serif",
								fontWeight: 800,
								color: colors.primary,
								fontSize: "1.4rem",
								mb: 0.4,
							}}>
							{t("signUp.title")}
						</Typography>
						<Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5, lineHeight: 1.5 }}>
							{t("signUp.subtitle")}
						</Typography>

						{errorMessageAlert && (
							<Alert severity="error" onClose={handleCloseErrorAlert} sx={{ mb: 2, py: 0.5 }}>
								{errorMessageAlert}
							</Alert>
						)}

						<Formik
							initialValues={initialValues}
							validationSchema={signUpSchema}
							onSubmit={handleSubmit}>
							{({ errors, touched, values, handleChange, setFieldValue }) => {
								const phoneError =
									(
										errors.phone &&
										typeof errors.phone === "object" &&
										"e164" in errors.phone
									) ?
										(errors.phone as { e164?: string }).e164
										: undefined;

								return (
									<Form>
										<Grid container spacing={1.5}>
											<Grid item xs={6}>
												<TextField
													size="small"
													error={!!(errors?.firstName && touched?.firstName)}
													helperText={errors.firstName && touched.firstName && errors.firstName}
													name="firstName"
													required
													fullWidth
													id="firstName"
													label={t("signUp.labels.firstName")}
													autoFocus
													value={values.firstName}
													onChange={handleChange}
												/>
											</Grid>
											<Grid item xs={6}>
												<TextField
													size="small"
													error={!!(errors?.lastName && touched?.lastName)}
													helperText={errors.lastName && touched.lastName && errors.lastName}
													required
													fullWidth
													id="lastName"
													label={t("signUp.labels.lastName")}
													name="lastName"
													autoComplete="family-name"
													value={values.lastName}
													onChange={handleChange}
												/>
											</Grid>
											<Grid item xs={12}>
												<TextField
													size="small"
													error={!!(errors?.email && touched?.email)}
													helperText={errors.email && touched.email && errors.email}
													required
													fullWidth
													id="email"
													label={t("signUp.labels.email")}
													name="email"
													autoComplete="email"
													value={values.email}
													onChange={handleChange}
												/>
											</Grid>
											<Grid item xs={12}>
												<TextField
													size="small"
													error={!!(errors?.password && touched?.password)}
													helperText={errors.password && touched.password && errors.password}
													required
													fullWidth
													name="password"
													label={t("signUp.labels.password")}
													type="password"
													id="password"
													autoComplete="new-password"
													value={values.password}
													onChange={handleChange}
												/>
											</Grid>
											<Grid item xs={12}>
												<FormControl
													fullWidth
													size="small"
													error={
														!!(
															touched.address &&
															errors.address &&
															typeof errors.address === "object" &&
															"country" in errors.address &&
															(errors.address as { country?: string }).country
														)
													}>
													<InputLabel id="country-select-label">
														{t("signUp.labels.country")} *
													</InputLabel>
													<Select
														labelId="country-select-label"
														id="country-select"
														name="address.country"
														value={values.address?.country || ""}
														label={t("signUp.labels.country") + " *"}
														onChange={(e) => setFieldValue("address.country", e.target.value)}>
														<MenuItem value="">
															<em>{t("signUp.helpers.countrySelect")}</em>
														</MenuItem>
														{[...SUPPORTED_COUNTRIES]
															.sort((a, b) => a.name.localeCompare(b.name))
															.map((country) => (
																<MenuItem key={country.code} value={country.code}>
																	{country.name}
																</MenuItem>
															))}
													</Select>
													<FormHelperText>
														{(
															touched.address &&
															errors.address &&
															typeof errors.address === "object" &&
															"country" in errors.address &&
															(errors.address as { country?: string }).country
														) ?
															(errors.address as { country?: string }).country
															: t("signUp.helpers.countrySelect")}
													</FormHelperText>
												</FormControl>
											</Grid>
											<Grid item xs={12}>
												<PhoneInput
													defaultCountry="US"
													international
													value={(values.phone?.e164 ?? "") as string}
													onChange={(val) => setFieldValue("phone.e164", val || "")}
													className="phone-input-container"
													style={{ width: "100%" }}
													inputComponent={PhoneTextField}
													inputProps={{
														fullWidth: true,
														label: t("signUp.labels.phone"),
														variant: "outlined",
														size: "small",
														error: !!(touched.phone && phoneError),
													}}
												/>
												<FormHelperText
													error={!!(touched.phone && phoneError)}
													sx={{ mx: "14px", mt: "3px" }}>
													{touched.phone && phoneError
														? phoneError
														: t("signUp.helpers.phoneOptional")}
												</FormHelperText>
											</Grid>
										</Grid>

										{/* Terms checkbox */}
										<FormControlLabel
											control={
												<Checkbox
													size="small"
													value="remember"
													sx={{
														color: colors.mainSecondary,
														"&.Mui-checked": { color: colors.mainSecondary },
													}}
													onChange={handleShowTerms}
												/>
											}
											label={
												<Typography variant="caption" sx={{ lineHeight: 1.4 }}>
													{t("signUp.checkboxes.termsConditions")}
												</Typography>
											}
											sx={{ mt: 1.5, mb: 0.5, alignItems: "flex-start" }}
										/>

										{/* SMS consent */}
										<FormControlLabel
											control={
												<Checkbox
													size="small"
													checked={hasSmsConsent}
													onChange={(e) => setHasSmsConsent(e.target.checked)}
													sx={{
														color: colors.mainSecondary,
														"&.Mui-checked": { color: colors.mainSecondary },
													}}
												/>
											}
											label={
												<Typography variant="caption" sx={{ lineHeight: 1.4 }}>
													I agree to receive transactional SMS payment reminders
													and account notifications from FundsyFi at the number
													provided. Message frequency varies; message &amp; data
													rates may apply. Reply STOP to opt out, HELP for help.
													See our{" "}
													<Link
														href="/privacy-policy"
														target="_blank"
														rel="noopener noreferrer">
														Privacy Policy
													</Link>{" "}
													and{" "}
													<Link
														href="/terms-of-service"
														target="_blank"
														rel="noopener noreferrer">
														Terms of Service
													</Link>
													. (optional)
												</Typography>
											}
											sx={{ mb: 0.5, alignItems: "flex-start" }}
										/>

										<UserTermsModal
											title={t("signUp.links.terms")}
											open={showTerms}
											handleClose={handleCloseShowTerms}
											onAgree={handleAgreeToTerms}
											onDisAgree={handleDisAgreeToTerms}
										/>

										{/* Submit button */}
										<Button
											type="submit"
											fullWidth
											variant="contained"
											sx={{
												mt: 2,
												mb: 1,
												bgcolor: colors.mainSecondary,
												color: colors.primary,
												fontWeight: 800,
												py: 1.2,
												borderRadius: 2,
												textTransform: "none",
												fontSize: "0.95rem",
												letterSpacing: 0.3,
												boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
												"&:hover": {
													bgcolor: colors.mainSecondary,
													boxShadow: `0 6px 20px ${alpha(colors.mainSecondary, 0.5)}`,
												},
											}}>
											{isLoading ?
												<CircularProgress size={22} sx={{ color: colors.primary }} />
												: t("signUp.buttons.signup")}
										</Button>

										{/* Sign in link */}
										<Box sx={{ textAlign: "center" }}>
											<Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
												{t("signUp.links.alreadyHaveAccount")}{" "}
												<Box
													component="span"
													onClick={handleRedirectToSignIn}
													sx={{
														color: colors.mainSecondary,
														fontWeight: 700,
														cursor: "pointer",
														"&:hover": { textDecoration: "underline" },
													}}>
													{t("signUp.links.signIn")}
												</Box>
											</Typography>
										</Box>
									</Form>
								);
							}}
						</Formik>

						<Typography
							variant="caption"
							sx={{ display: "block", textAlign: "center", mt: 2.5, color: "text.disabled" }}>
							© {new Date().getFullYear()} FundsyFi
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default SignUp;

